import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Subject, finalize, takeUntil } from "rxjs";
import { IResetPassword } from "src/app/shared/interfaces/user.interface";
import { UserPasswordService } from "src/app/shared/services/user-password.service";
import Validation from "src/app/shared/utils/helpers/validators/form-field-custom.validators";

@Component({
  selector: "app-reset-password-form",
  templateUrl: "./reset-password-form.component.html",
  styleUrls: ["./reset-password-form.component.scss"],
})
export class ResetPasswordFormComponent implements OnInit, OnDestroy {
  /** Reset code provided by the caller (e.g. read from the reset link), opening the form directly on the code step */
  @Input() code?: string;
  @Input() showBorder = false;

  @Output() resetPasswordFormSubmit: EventEmitter<void> = new EventEmitter<void>();
  @Output() backClick: EventEmitter<void> = new EventEmitter<void>();

  public emailForm = this.fb.nonNullable.group({
    email: ["", [Validators.required, Validators.pattern(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)]],
  });
  public codeForm = this.fb.nonNullable.group(
    {
      code: ["", [Validators.required]],
      password: ["", [Validators.required, Validation.password()]],
      confirmPassword: ["", [Validators.required]],
    },
    {
      validators: [Validation.match("password", "confirmPassword")],
    }
  );

  public hasCode = false;
  public showPassword = false;
  public showConfirmPassword = false;

  private onDestroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private userPasswordService: UserPasswordService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {}

  /**
   * Opens the form directly on the code step, with the code filled in, when a code is provided.
   */
  ngOnInit(): void {
    if (this.code) {
      this.hasCode = true;
      this.codeForm.patchValue({
        code: this.code,
      });
    }
  }

  /**
   * Cleanup subscriptions on destroy.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Notifies the parent that the user cancelled the reset password flow.
   */
  public goBack(): void {
    this.backClick.emit();
  }

  /**
   * Returns from the code entry step to the email step, clearing and hiding both passwords again
   * so they aren't redisplayed when coming back to the code entry step. The code is kept so the
   * user doesn't have to type it again if they come back with the same one.
   */
  public backToEmailStep(): void {
    this.hasCode = false;
    this.codeForm.controls.password.reset();
    this.codeForm.controls.confirmPassword.reset();
    this.showPassword = false;
    this.showConfirmPassword = false;
  }

  /**
   * Submits the current step of the reset password flow. An invalid active form is marked as
   * touched instead of being submitted. Otherwise: while no code has been requested yet, sends a
   * reset email to the entered address and, on success, clears the now stale code and switches to
   * the code entry step; once a code has been requested, submits it together with the new password
   * and emits resetPasswordFormSubmit on success. The active form is disabled while its request is
   * in flight and re-enabled once it settles, and a snackbar reports the outcome either way.
   */
  public onSubmit(): void {
    if (!this.hasCode) {
      if (this.emailForm.invalid) {
        this.emailForm.markAllAsTouched();
        return;
      }

      this.emailForm.disable();
      this.userPasswordService
        .forgotPassword(this.emailForm.controls.email.value)
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => this.emailForm.enable())
        )
        .subscribe({
          next: () => {
            this.snackBar.open(`✅ ${this.translateService.instant("resetPassword.emailSuccess")}`, "OK", {
              duration: 5000,
            });
            // a new email means a new code, so any previously entered or provided one is now stale
            this.codeForm.controls.code.reset();
            this.hasCode = true;
          },
          error: (err) => {
            if (err.status === 400) {
              this.snackBar.open(`❗ ${this.translateService.instant("resetPassword.emailError")}`, "OK", {
                duration: 5000,
              });
            } else {
              this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
                duration: 5000,
              });
            }
          },
        });
    } else {
      if (this.codeForm.invalid) {
        this.codeForm.markAllAsTouched();
        return;
      }

      this.codeForm.disable();
      const resetPassword: IResetPassword = {
        token: this.codeForm.controls.code.value,
        password: this.codeForm.controls.password.value,
      };
      this.userPasswordService
        .resetPassword(resetPassword)
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => this.codeForm.enable())
        )
        .subscribe({
          next: () => {
            this.snackBar.open(`✅ ${this.translateService.instant("resetPassword.resetSuccess")}`, "OK", {
              duration: 5000,
            });
            this.resetPasswordFormSubmit.emit();
          },
          error: (err) => {
            if (err.status === 400) {
              this.snackBar.open(`❗ ${this.translateService.instant("resetPassword.codeError")}`, "OK", {
                duration: 5000,
              });
            } else {
              this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
                duration: 5000,
              });
            }
          },
        });
    }
  }
}
